import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/hooks/useLanguage';

interface AccordionItem {
    id: string;
    question: string;
    answer: string;
}

const FAQ_ITEMS: AccordionItem[] = [
    {
        id: 'faq-1',
        question: 'What is the Power Analysis Dashboard?',
        answer: 'It monitors real-time electrical metrics (voltage, current, power factor, demand, active/reactive energy) collected from Raspberry Pi edge nodes installed at the site.',
    },
    {
        id: 'faq-2',
        question: 'What do the green/orange/red indicators mean?',
        answer: 'Green means the Raspberry Pi edge node is online and reporting health telemetry normally. Orange/red indicate potential communication delays or high temperature/resource throttling.',
    },
    {
        id: 'faq-3',
        question: 'How are billing demand charges calculated?',
        answer: 'Demand charges are based on the Maximum Demand (MD) recorded in 15-minute integration windows, calculated according to the active tariff structure (e.g., TGSPDCL HT or WBSEDCL B-IDIT).',
    },
    {
        id: 'faq-4',
        question: 'What should I do if the dashboard shows "No data"?',
        answer: 'Verify that the edge node (Raspberry Pi gateway) is powered on and connected to the internet. If the issue persists, contact technical support immediately.',
    },
];

export function QuickHelpAccordion() {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const { t } = useLanguage();

    const toggleItem = (index: number) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className="divide-y divide-ink-100 border-b border-ink-100">
            {FAQ_ITEMS.map((item, idx) => {
                const isOpen = activeIndex === idx;
                return (
                    <div key={item.id} className="py-2">
                        <h3>
                            <button
                                id={`${item.id}-btn`}
                                type="button"
                                onClick={() => toggleItem(idx)}
                                className="flex w-full items-center justify-between gap-4 py-3 text-left transition hover:text-brand-500"
                                aria-expanded={isOpen}
                                aria-controls={item.id}
                            >
                                <span className="text-[13px] font-medium text-ink-800 leading-snug">
                                    {t(item.question)}
                                </span>
                                <ChevronDown
                                    className={cn(
                                        'h-4 w-4 shrink-0 text-ink-400 transition-transform duration-200',
                                        isOpen && 'rotate-180 text-brand-500'
                                    )}
                                />
                            </button>
                        </h3>
                        <div
                            id={item.id}
                            role="region"
                            aria-labelledby={`${item.id}-btn`}
                            className={cn(
                                'transition-all duration-200 ease-in-out overflow-hidden',
                                isOpen ? 'max-h-40 mt-1 pb-3' : 'max-h-0'
                            )}
                        >
                            <div className="text-[11px] text-ink-500 leading-relaxed">
                                {t(item.answer)}
                             </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

